import { FeaturePageLayout } from '../../components/features/FeaturePageLayout';
import { DashboardMockup, KpiMockup } from '../../components/features/ProductMockups';

const RELATED = [
  { label: 'Prévision de trésorerie', href: '/fonctionnalites/prevision-tresorerie' },
  { label: 'Alertes', href: '/fonctionnalites/alertes-tresorerie' },
  { label: 'Reporting financier', href: '/fonctionnalites/reporting-financier' },
  { label: 'Stress test', href: '/fonctionnalites/stress-test-tresorerie' },
];

interface Props { onNavigate?: (p: string) => void; onSignIn?: () => void; onSignUp?: () => void; }

export default function TableauBordTempsReelPage({ onNavigate, onSignIn, onSignUp }: Props) {
  return (
    <FeaturePageLayout
      seo={{
        title: 'Tableau de bord temps réel — Vue instantanée de votre trésorerie | TrezoOne',
        description: 'Visualisez votre trésorerie en temps réel : soldes, encaissements, décaissements, score de pilotage et alertes actives. Tout dans un tableau de bord clair et actionnable.',
        canonical: '/fonctionnalites/tableau-bord-temps-reel',
        keywords: 'tableau de bord trésorerie, vue temps réel finances, solde bancaire, encaissements décaissements, pilotage financier',
      }}
      activeNavId="dashboard"
      hero={{
        tag: 'Tableau de bord temps réel',
        title: 'Votre situation financière complète, en un coup d\'œil.',
        subtitle: 'Dès que vous ouvrez TrezoOne, vous savez exactement où vous en êtes : solde disponible, résultat du mois, encaissements, décaissements, alertes actives et score de pilotage global.',
        mockup: <DashboardMockup />,
      }}
      problem={{
        title: 'Vous n\'avez pas de vue d\'ensemble claire de votre situation financière',
        points: [
          'Vous jongglez entre plusieurs fichiers Excel, relevés bancaires et tableurs pour avoir une vision globale.',
          'Votre situation financière n\'est jamais à jour — vous travaillez toujours avec des données de la semaine passée.',
          'Vous ne savez pas d\'un coup d\'œil si votre mois est bon ou mauvais.',
          'Impossible de partager une vue claire avec votre équipe ou votre CA sans refaire une mise en forme manuelle.',
        ],
      }}
      steps={[
        { num: 1, title: 'Importez vos transactions', desc: 'Chargez votre relevé bancaire en CSV, OFX ou PDF. TrezoOne intègre toutes les opérations instantanément.' },
        { num: 2, title: 'Catégorisez vos flux', desc: 'TrezoOne suggère automatiquement les catégories. Validez ou ajustez en quelques clics.' },
        { num: 3, title: 'Votre tableau de bord se met à jour', desc: 'Tous les indicateurs sont recalculés instantanément : solde, résultat, encaissements, décaissements, score.' },
        { num: 4, title: 'Pilotez et partagez', desc: 'Consultez votre tableau de bord à tout moment, depuis n\'importe quel appareil. Exportez ou partagez en un clic.' },
      ]}
      screens={[<DashboardMockup />, <KpiMockup />]}
      useCases={[
        { audience: 'Associations', benefit: 'Ayez une vue claire et à jour de votre trésorerie avant chaque réunion de bureau ou de CA.' },
        { audience: 'PME', benefit: 'Suivez vos performances financières sans attendre le bilan de votre comptable.' },
        { audience: 'Dirigeant', benefit: 'Consultez les chiffres clés en 30 secondes, depuis votre smartphone ou votre bureau.' },
        { audience: 'Trésorier(e)', benefit: 'Gérez plusieurs comptes avec une vue consolidée sans tableau croisé dynamique.' },
        { audience: 'RAF / DAF', benefit: 'Accédez à une synthèse financière opérationnelle actualisée à chaque import.' },
        { audience: 'Comptable', benefit: 'Complétez votre suivi comptable avec une vision cash quotidienne facile à lire.' },
      ]}
      benefits={[
        'Connaître votre situation financière exacte à tout moment',
        'Réduire le temps de consultation de 30 minutes à 30 secondes',
        'Partager une vue financière claire à votre équipe ou votre CA',
        'Détecter les anomalies instantanément grâce aux alertes visuelles',
        'Prendre des micro-décisions quotidiennes sur une base chiffrée',
        'Supprimer les fichiers Excel de suivi redondants',
      ]}
      faq={[
        { q: 'Le tableau de bord est-il accessible sur mobile ?', a: 'Oui. TrezoOne est responsive et fonctionne sur tous les écrans : ordinateur, tablette et smartphone.' },
        { q: 'Puis-je gérer plusieurs comptes bancaires ?', a: 'Oui. Vous pouvez connecter plusieurs comptes de trésorerie et visualiser une vue consolidée ou par compte.' },
        { q: 'Comment les données sont-elles mises à jour ?', a: 'Les données sont mises à jour à chaque import de relevé bancaire. TrezoOne recalcule instantanément tous les indicateurs.' },
        { q: 'Le tableau de bord est-il personnalisable ?', a: 'Les indicateurs principaux sont standards mais vous pouvez configurer vos catégories, vos seuils d\'alerte et vos périodes de référence.' },
      ]}
      relatedLinks={RELATED}
      onNavigate={onNavigate}
      onSignIn={onSignIn}
      onSignUp={onSignUp}
    />
  );
}
